import { supabase } from '@/lib/supabase/client';
import { createNotFoundError, fromPostgrestError, normalizeError } from '@/lib/services/errors';
import type { Database, Json } from '@/lib/supabase/types.gen';
import type {
  PostgrestError,
  PostgrestMaybeSingleResponse,
  PostgrestResponse,
  PostgrestSingleResponse,
} from '@supabase/postgrest-js';
import type {
  FloorPlanCanvasConfig,
  PaginationInput,
  PropertyDetailResult,
  PropertyFormValues,
  PropertyListFilters,
  PropertyListMetrics,
  PropertyListResult,
  PropertyListSorting,
  PropertySummary,
} from '@/lib/types/property';
import {
  LEASEABLE_UNIT_STATUSES,
  createPropertyUnitAggregate,
  mapFloorRowToRecord,
  mapUnitRowToSummary,
  type PropertyUnitAggregate,
  type UnitRow,
} from '@/lib/supabase/unitMappers';

const DEFAULT_PAGE_SIZE = 25;
const MAX_PAGE_SIZE = 100;

type PropertiesTable = Database['public']['Tables']['properties'];
type PropertyRow = PropertiesTable['Row'];
type PropertyInsert = PropertiesTable['Insert'];
type PropertyUpdate = PropertiesTable['Update'];
type PropertyFloorRow = Database['public']['Tables']['property_floors']['Row'];
type PropertyRowWithRelations = PropertyRow & {
  units?: UnitRow[] | null;
  property_floors?: PropertyFloorRow[] | null;
};

export async function listProperties({
  pagination,
  filters,
  signal,
  sort,
}: ListPropertiesParams): Promise<PropertyListResult> {
  try {
    const page = Math.max(1, pagination.page);
    const pageSize = clampPageSize(pagination.pageSize);
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    const includePropertyIds = await findPropertyIdsMatchingUnitFilters(filters);
    if (includePropertyIds?.length === 0) {
      return createEmptyListResult(page, pageSize);
    }

    let query = createPropertiesQuery().select('*, units:units(*)', { count: 'exact' });

    if (filters?.submarkets && filters.submarkets.length > 0) {
      query = query.in('submarket', filters.submarkets);
    }

    if (filters?.assetTypes && filters.assetTypes.length > 0) {
      query = query.in('asset_type', filters.assetTypes);
    }

    if (includePropertyIds && includePropertyIds.length > 0) {
      query = query.in('id', includePropertyIds);
    }

    if (filters?.hasVacancy === false) {
      const vacancyIds = await findPropertyIdsWithVacancy();
      if (vacancyIds.length > 0) {
        query = query.not('id', 'in', formatIdsForNotIn(vacancyIds));
      }
    }

    const sortColumn = mapSortColumnToDatabase(sort?.column ?? 'propertyCode');
    const sortDirection = sort?.direction === 'desc' ? { ascending: false } : { ascending: true };
    query = query.order(sortColumn, sortDirection);
    query = query.range(from, to);

    if (signal) {
      query = query.abortSignal(signal);
    }

    const response = (await query.throwOnError()) as PostgrestResponse<PropertyRowWithRelations>;
    const { data, count } = response;
    const rows: PropertyRowWithRelations[] = Array.isArray(data) ? data : [];

    const items = rows.map((row) => {
      const aggregate = createPropertyUnitAggregate(Array.isArray(row.units) ? row.units : []);
      return mapPropertyRowToSummary(row, aggregate);
    });
    const totalItems = typeof count === 'number' ? count : items.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const metrics = computePropertyListMetrics(items, totalItems);

    return {
      items,
      totalItems,
      totalPages,
      page,
      pageSize,
      metrics,
    };
  } catch (error) {
    handleSupabaseError(error);
  }
}

export async function getPropertyById(propertyId: string): Promise<PropertyDetailResult> {
  try {
    return await fetchPropertyDetail(propertyId);
  } catch (error) {
    handleSupabaseError(error);
  }
}

export async function createProperty(
  values: PropertyFormValues,
  userId: string,
): Promise<PropertyDetailResult> {
  try {
    const payload = mapPropertyFormToInsert(values, userId);
    const response = (await createPropertiesQuery()
      .insert(payload)
      .select('*')
      .single()
      .throwOnError()) as PostgrestSingleResponse<PropertyRow>;
    const { data } = response;

    if (!data || typeof data.id !== 'string') {
      throw createNotFoundError('Property could not be created.');
    }

    return await fetchPropertyDetail(data.id);
  } catch (error) {
    handleSupabaseError(error);
  }
}

export async function updateProperty(
  propertyId: string,
  values: PropertyFormValues,
  userId: string,
): Promise<PropertyDetailResult> {
  try {
    const payload = mapPropertyFormToUpdate(values, userId);
    const response = (await createPropertiesQuery()
      .update(payload)
      .eq('id', propertyId)
      .select('*')
      .single()
      .throwOnError()) as PostgrestSingleResponse<PropertyRow>;
    const { data } = response;

    if (!data || typeof data.id !== 'string') {
      throw createNotFoundError('Property not found.');
    }

    return await fetchPropertyDetail(data.id);
  } catch (error) {
    handleSupabaseError(error);
  }
}

export async function deleteProperty(propertyId: string): Promise<void> {
  try {
    const response = (await createPropertiesQuery()
      .delete()
      .eq('id', propertyId)
      .select('id')
      .single()
      .throwOnError()) as PostgrestSingleResponse<Pick<PropertyRow, 'id'>>;
    const { data } = response;

    if (!data || typeof data.id !== 'string') {
      throw createNotFoundError('Property not found.');
    }
  } catch (error) {
    handleSupabaseError(error);
  }
}

async function fetchPropertyDetail(propertyId: string): Promise<PropertyDetailResult> {
  const response = (await createPropertiesQuery()
    .select(
      `
        *,
        units:units(*),
        property_floors(*)
      `,
    )
    .eq('id', propertyId)
    .maybeSingle()
    .throwOnError()) as PostgrestMaybeSingleResponse<PropertyRowWithRelations>;
  const { data: propertyRow } = response;

  if (!propertyRow) {
    throw createNotFoundError('Property not found.');
  }

  const unitRows: UnitRow[] = Array.isArray(propertyRow.units) ? propertyRow.units : [];
  const floorRows: PropertyFloorRow[] = Array.isArray(propertyRow.property_floors)
    ? propertyRow.property_floors
    : [];
  const aggregate = createPropertyUnitAggregate(unitRows);

  return {
    property: mapPropertyRowToFormValues(propertyRow),
    summary: mapPropertyRowToSummary(propertyRow, aggregate),
    units: unitRows.map(mapUnitRowToSummary),
    floors: floorRows.map(mapFloorRowToRecord),
  };
}

function createEmptyListResult(page: number, pageSize: number): PropertyListResult {
  return {
    items: [],
    totalItems: 0,
    totalPages: 1,
    page,
    pageSize,
    metrics: {
      total: 0,
      withVacancy: 0,
    },
  };
}

function mapSortColumnToDatabase(column: PropertyListSorting['column']): string {
  switch (column) {
    case 'assetType':
      return 'asset_type';
    case 'submarket':
      return 'submarket';
    case 'totalRentableSf':
      return 'total_rentable_sf';
    case 'createdAt':
      return 'created_at';
    case 'updatedAt':
      return 'updated_at';
    case 'propertyCode':
    default:
      return 'property_code';
  }
}

async function findPropertyIdsMatchingUnitFilters(
  filters: PropertyListFilters | undefined,
): Promise<string[] | null> {
  if (!filters) {
    return null;
  }

  const requiresUnitFilters =
    filters.hasVacancy === true ||
    filters.incentivesOnly === true ||
    typeof filters.potentiallyDivisible === 'boolean' ||
    (Array.isArray(filters.readiness) && filters.readiness.length > 0) ||
    hasRangeValue(filters.rentableSquareFeet) ||
    hasRangeValue(filters.rentPerSquareFoot) ||
    hasDateRange(filters.availability);

  if (!requiresUnitFilters) {
    return null;
  }

  let query = createUnitsQuery().select('property_id');

  if (filters.hasVacancy === true) {
    query = query.in('availability_status', LEASEABLE_UNIT_STATUSES);
  }

  if (Array.isArray(filters.readiness) && filters.readiness.length > 0) {
    query = query.in('availability_status', filters.readiness);
  }

  if (filters.incentivesOnly === true) {
    query = query.not('incentives', 'is', null);
  }

  if (typeof filters.potentiallyDivisible === 'boolean') {
    query = query.eq('potentially_divisible', filters.potentiallyDivisible);
  }

  const rentRange = filters.rentPerSquareFoot;
  if (rentRange?.min != null) {
    query = query.gte('net_rent_per_sf', rentRange.min);
  }
  if (rentRange?.max != null) {
    query = query.lte('net_rent_per_sf', rentRange.max);
  }

  const sfRange = filters.rentableSquareFeet;
  if (sfRange?.min != null) {
    query = query.gte('area_sf', sfRange.min);
  }
  if (sfRange?.max != null) {
    query = query.lte('area_sf', sfRange.max);
  }

  const availabilityStart = filters.availability?.start;
  if (typeof availabilityStart === 'string' && availabilityStart.length > 0) {
    query = query.gte('availability_start', availabilityStart);
  }
  const availabilityEnd = filters.availability?.end;
  if (typeof availabilityEnd === 'string' && availabilityEnd.length > 0) {
    query = query.lte('availability_start', availabilityEnd);
  }

  const response = (await query.throwOnError()) as PostgrestResponse<Pick<UnitRow, 'property_id'>>;
  const { data } = response;
  const rows: Pick<UnitRow, 'property_id'>[] = Array.isArray(data) ? data : [];
  const propertyIds = rows
    .map((row) => row.property_id)
    .filter((value): value is string => typeof value === 'string' && value.length > 0);
  return Array.from(new Set(propertyIds));
}

async function findPropertyIdsWithVacancy(): Promise<string[]> {
  const response = (await createUnitsQuery()
    .select('property_id')
    .in('availability_status', LEASEABLE_UNIT_STATUSES)
    .throwOnError()) as PostgrestResponse<Pick<UnitRow, 'property_id'>>;
  const { data } = response;

  const rows: Pick<UnitRow, 'property_id'>[] = Array.isArray(data) ? data : [];
  const propertyIds = rows
    .map((row) => row.property_id)
    .filter((value): value is string => typeof value === 'string' && value.length > 0);
  return Array.from(new Set(propertyIds));
}

function computePropertyListMetrics(
  items: PropertySummary[],
  totalItems: number,
): PropertyListMetrics {
  const withVacancy = items.reduce((count, item) => count + (item.hasVacancy ? 1 : 0), 0);

  return {
    total: totalItems,
    withVacancy,
  };
}

function mapPropertyRowToFormValues(row: PropertyRow): PropertyFormValues {
  return {
    id: row.id,
    propertyCode: row.property_code,
    submarket: row.submarket,
    assetType: row.asset_type,
    totalRentableSf: row.total_rentable_sf,
    marketingSummary: row.marketing_summary,
    heroMediaUrls: row.hero_media_urls,
    floorPlanMode: row.floor_plan_mode,
    floorPlanImageUrl: row.floor_plan_image_url,
    floorPlanConfig: parseFloorPlanConfig(row.floor_plan_config),
    address: mapAddress(row),
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    createdBy: row.created_by,
    updatedBy: row.updated_by,
  };
}

function mapPropertyRowToSummary(
  row: PropertyRow,
  aggregate: PropertyUnitAggregate,
): PropertySummary {
  return {
    id: row.id,
    propertyCode: row.property_code,
    address: mapAddress(row),
    submarket: row.submarket,
    assetType: row.asset_type,
    totalRentableSf: row.total_rentable_sf,
    marketingSummary: row.marketing_summary,
    heroMediaUrls: row.hero_media_urls,
    floorPlanMode: row.floor_plan_mode,
    floorPlanImageUrl: row.floor_plan_image_url,
    floorPlanConfig: parseFloorPlanConfig(row.floor_plan_config),
    publishedUnitCount: aggregate.publishedUnitCount,
    hasVacancy: aggregate.hasVacancy,
    hasIncentives: aggregate.hasIncentives,
    hasPotentiallyDivisible: aggregate.hasPotentiallyDivisible,
    readinessStates: aggregate.readinessStates,
    minUnitRentableSf: aggregate.minUnitRentableSf,
    maxUnitRentableSf: aggregate.maxUnitRentableSf,
    minNetRent: aggregate.minNetRent,
    maxNetRent: aggregate.maxNetRent,
    minAdditionalRent: aggregate.minAdditionalRent,
    maxAdditionalRent: aggregate.maxAdditionalRent,
    earliestAvailability: aggregate.earliestAvailability,
    latestAvailability: aggregate.latestAvailability,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapPropertyFormToInsert(values: PropertyFormValues, userId: string): PropertyInsert {
  return {
    ...mapPropertyFormToRow(values),
    created_by: userId,
    updated_by: userId,
  };
}

function mapPropertyFormToUpdate(values: PropertyFormValues, userId: string): PropertyUpdate {
  return {
    ...mapPropertyFormToRow(values),
    updated_by: userId,
  };
}

function mapPropertyFormToRow(
  values: PropertyFormValues,
): Omit<PropertyInsert, 'created_by' | 'updated_by'> {
  return {
    property_code: sanitizePropertyCode(values.propertyCode),
    submarket: values.submarket,
    asset_type: values.assetType,
    total_rentable_sf: values.totalRentableSf,
    marketing_summary: values.marketingSummary,
    hero_media_urls: sanitizeHeroMediaUrls(values.heroMediaUrls),
    floor_plan_mode: values.floorPlanMode,
    floor_plan_image_url: values.floorPlanImageUrl,
    floor_plan_config: sanitizeFloorPlanConfig(values.floorPlanConfig),
    street_number: values.address.streetNumber,
    street_name: values.address.streetName,
    street_type: values.address.streetType,
    street_direction: values.address.streetDirection ?? null,
    suite_number: values.address.suiteNumber ?? null,
    city: values.address.city,
    province: values.address.province,
    postal_code: values.address.postalCode,
  };
}

function mapAddress(row: PropertyRow) {
  return {
    streetNumber: row.street_number,
    streetName: row.street_name,
    streetType: row.street_type,
    streetDirection: row.street_direction,
    suiteNumber: row.suite_number,
    city: row.city,
    province: row.province,
    postalCode: row.postal_code,
  };
}

function parseFloorPlanConfig(value: Json | null): FloorPlanCanvasConfig {
  if (value !== null && typeof value === 'object') {
    return value as FloorPlanCanvasConfig;
  }
  return {};
}

function sanitizeFloorPlanConfig(config: FloorPlanCanvasConfig): Json {
  try {
    return JSON.parse(JSON.stringify(config)) as Json;
  } catch {
    return {};
  }
}

function sanitizeHeroMediaUrls(urls: string[] | undefined): string[] {
  if (!Array.isArray(urls)) {
    return [];
  }
  return urls.filter((url): url is string => typeof url === 'string' && url.length > 0);
}

function sanitizePropertyCode(code: string): string {
  return code.trim().toUpperCase();
}

function hasRangeValue(range?: { min?: number | null; max?: number | null } | null): boolean {
  if (!range) {
    return false;
  }
  return range.min != null || range.max != null;
}

function hasDateRange(range?: { start?: string | null; end?: string | null } | null): boolean {
  if (!range) {
    return false;
  }
  const hasStart = typeof range.start === 'string' && range.start.length > 0;
  const hasEnd = typeof range.end === 'string' && range.end.length > 0;
  return hasStart || hasEnd;
}

function clampPageSize(size: number): number {
  if (!Number.isFinite(size) || size <= 0) {
    return DEFAULT_PAGE_SIZE;
  }
  return Math.min(Math.floor(size), MAX_PAGE_SIZE);
}

function formatIdsForNotIn(ids: string[]): string {
  return `(${ids.map((id) => `"${id}"`).join(',')})`;
}

function handleSupabaseError(error: unknown): never {
  if (isPostgrestError(error)) {
    throw fromPostgrestError(error);
  }
  throw normalizeError(error);
}

function isPostgrestError(error: unknown): error is PostgrestError {
  return typeof error === 'object' && error !== null && 'code' in error && 'message' in error;
}

function createPropertiesQuery() {
  return supabase.from('properties');
}

function createUnitsQuery() {
  return supabase.from('units');
}

export interface ListPropertiesParams {
  pagination: PaginationInput;
  filters?: PropertyListFilters;
  signal?: AbortSignal;
  sort?: PropertyListSorting;
}
